import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';

import { AuthService } from '../../services/auth.service';
import { UserProfileService } from '../../services/user-profile.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [ReactiveFormsModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  protected readonly userProfile = inject(UserProfileService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  // Manage the view/edit state
  isEditing = signal(false);

  /** Goal dropdown options: 10 to 20 */
  readonly goalOptions = Array.from({ length: 11 }, (_, i) => 10 + i);

  // Set up the reactive form
  profileForm = this.fb.nonNullable.group({
    displayName: [''],
    examDate: [''],
    goal: [10, [Validators.min(10), Validators.max(20)]],
  });

  ngOnInit(): void {
    if (!this.auth.isAuthenticated()) {
      this.router.navigate(['/']);
      return;
    }
    const currentProfile = this.userProfile.profile();
    if (!currentProfile && !this.userProfile.loading()) {
      this.userProfile.fetchProfile();
    }
  }

  startEdit(profile: { displayName?: string | null; examDate?: string | null; goal?: number | null }): void {
    this.userProfile.setError(null);
    this.profileForm.patchValue({
      displayName: profile.displayName ?? '',
      examDate: profile.examDate ?? '',
      goal: profile.goal ?? 10,
    });
    this.isEditing.set(true);
  }

  cancelEdit(): void {
    this.profileForm.reset();
    this.isEditing.set(false);
  }

  saveEdit(): void {
    if (!this.profileForm.valid) return;
    const profile = this.userProfile.profile();
    if (!profile) return;

    const { displayName, examDate, goal } = this.profileForm.getRawValue();
    this.userProfile.updateProfile(profile.userId, {
      displayName: displayName || null,
      examDate: examDate || null,
      goal,
      subscriptionTier: profile.subscriptionTier,
    }).subscribe({
      next: () => {
        this.isEditing.set(false);
      },
      error: (err) => {
        this.userProfile.setError(err?.error?.detail ?? err?.message ?? 'Failed to update profile');
      },
    });
  }
}